import Vue from 'vue';
import VueRouter from 'vue-router';
import routes from './routes';

import storage from '../utils/storage';
import utils from '../utils/general';

import store from '../vuex';
import { OPTIONS_STORE_ACCESS } from '../vuex/module/access';
import { PROFILES } from '../../../common/constants/generic/types';
import { isProductionEnvironment } from '@/utils/environment';
import { ROUTE_NAMES } from '../constants/route-constants';
import { ACCESS_VALUES } from '../../../common/constants/global';

Vue.use(VueRouter);

function hasRouteAccess(accessCode, userAccessList) {
  if (!accessCode) return true;

  if (!userAccessList || userAccessList.length === 0) return false;

  const userAccess = userAccessList.find(a => a.code === accessCode);

  if (!userAccess) return false;

  return userAccess.value === ACCESS_VALUES.ENABLED;
}

const customerAccessMode = () => {
  const user = storage.getUserOnStorage();

  if (user?.customerId && Number(user?.profileId) === PROFILES.CUSTOMER.id) {
    return true;
  }

  const customer = storage.getCustomerOnStorage();

  if (customer?.id && !user) {
    return true;
  }

  if (!user && !customer) {
    const token = storage.getTokenOnStorage();

    if (token) {
      try {
        const tokenData = utils.parseJwt(token);

        if (tokenData.id && tokenData.registerStatus !== undefined) {
          return true;
        }
      } catch (e) {
        // Token inválido, ignorar
      }
    }
  }

  return false;
};

/* Vencido pelo `exp` do JWT; token que nem decodifica tambem conta como vencido. */
function isTokenExpired(token) {
  try {
    const tokenData = utils.parseJwt(token);

    return !!tokenData.exp && (tokenData.exp * 1000) < Date.now();
  } catch (e) {
    return true;
  }
}

const CUSTOMER_ACCESS_ROUTES = [
  ROUTE_NAMES.CUSTOMER_ACCESS_HOME,
  ROUTE_NAMES.CUSTOMER_ACCESS_OPERATIONS,
  ROUTE_NAMES.CUSTOMER_ACCESS_OPERATION_REPORT,
  ROUTE_NAMES.CUSTOMER_ACCESS_ACCOUNT,
  ROUTE_NAMES.CUSTOMER_ACCESS_OPERATION_DETAIL,
  ROUTE_NAMES.CUSTOMER_ACCESS_WALLETS,
  ROUTE_NAMES.PAGE_NOT_PERMISSION,
  ROUTE_NAMES.FILE_VIEW,
  ROUTE_NAMES.FORM_EDIT_CUSTOMER_EXTERNAL_ACCESS,
  ROUTE_NAMES.CUSTOMER_SELFIE_EXTERNAL_ACCESS,
  ROUTE_NAMES.FORM_CUSTOMER_FINALLY,
  ROUTE_NAMES.CUSTOMER_DASHBOARD
];

const PUBLIC_ROUTES = [
  ROUTE_NAMES.ASSINATURA,
  ROUTE_NAMES.LOGIN,
  ROUTE_NAMES.CONFIRMACAO_ASSINATURA,
  ROUTE_NAMES.CUSTOMER_SELFIE_EXTERNAL_ACCESS,
  ROUTE_NAMES.CUSTOMER_OPERATION_APPROVAL
];

const SUPER_ADMIN_ONLY_ROUTES = [
  'ProfilesControl',
  'FormCreateProfile',
  'FormEditProfile',
  'UIKit',
  'InterbanksControl'
];

/* Ferramenta de quem constroi o produto, nao dele: fora de producao apenas.
   InterbanksControl e tela simulada (lista perfis de acesso no lugar da API que
   ainda nao existe): fica so para o teste e2e fora da producao. */
const NON_PRODUCTION_ONLY_ROUTES = ['UIKit', 'InterbanksControl'];

const router = new VueRouter({
  mode: 'history',
  base: import.meta.env.BASE_URL,
  routes
});

// It's important to make sure that next() is called exactly once in any given pass through the navigation guard
router.beforeEach(async (to, from, next) => {
  if (window.__SW_UPDATED) {
    window.location.reload();

    return;
  }

  const matched = to.matched;
  const hasPublicMeta = matched.some((record) => record.meta.requireAuth === false);
  const isPublicRoute = PUBLIC_ROUTES.includes(to.name) || hasPublicMeta;
  const token = localStorage.getItem('token-tc');
  const logged = token != null;

  // Se indo pro login com token existente, verificar se token nao expirou
  if (to.name === ROUTE_NAMES.LOGIN && logged) {
    if (isTokenExpired(token)) {
      storage.removeAuth();
      next();

      return;
    }

    if (customerAccessMode()) {
      next({ name: ROUTE_NAMES.CUSTOMER_ACCESS_HOME });
    } else {
      next({ name: ROUTE_NAMES.HOME });
    }

    return;
  }

  if (isPublicRoute) {
    next();

    return;
  }

  const requireAuth = matched.some((record) => record.meta.requireAuth);

  /* Token vencido (ou ilegivel) em rota protegida conta como deslogado. Sem
     isso o guard deixava passar, a tela quebrava no 401 e o destino se perdia
     (ex.: entrada do portal DeepDesk em /suporte). Ordem importa: removeAuth
     antes de saveReturnPath. */
  if (requireAuth && logged && isTokenExpired(token)) {
    storage.removeAuth();
    storage.saveReturnPath(to.fullPath);
    next({ name: ROUTE_NAMES.LOGIN });

    return;
  }

  if (customerAccessMode()) {
    if (to.name === ROUTE_NAMES.DASHBOARD) {
      next({ name: ROUTE_NAMES.CUSTOMER_DASHBOARD });

      return;
    }

    if (!CUSTOMER_ACCESS_ROUTES.includes(to.name)) {
      next({ name: ROUTE_NAMES.PAGE_NOT_PERMISSION });

      return;
    }
  }

  const loggedUser = storage.getUserOnStorage();
  const isCustomerAccessingDashboard = loggedUser
    && Number(loggedUser?.profileId) === PROFILES.CUSTOMER.id
    && to.name === ROUTE_NAMES.DASHBOARD;

  if (isCustomerAccessingDashboard)
    return next({ name: ROUTE_NAMES.CUSTOMER_DASHBOARD });

  if (matched.length == 0) {
    if (logged) {
      next({ name: ROUTE_NAMES.PAGE_NOT_FOUND });
    } else {
      next({ name: ROUTE_NAMES.LOGIN });
    }

    return;
  }

  if (requireAuth) {
    if (logged) {
      const user = storage.getUserOnStorage();
      const accessCode = to.meta.accessCode;
      let userAccess = store.getters[OPTIONS_STORE_ACCESS.GETTERS.GET_ACCESS];

      if (!userAccess || userAccess.length === 0) {
        await store.dispatch(OPTIONS_STORE_ACCESS.ACTIONS.FIND_ACCESS, user);
        userAccess = store.getters[OPTIONS_STORE_ACCESS.GETTERS.GET_ACCESS];
      }

      if (accessCode) {
        const hasAccess = hasRouteAccess(accessCode, userAccess);

        if (!hasAccess) {
          next({ name: 'PageNotPermission' });

          return;
        }
      }

      if (NON_PRODUCTION_ONLY_ROUTES.includes(to.name) && isProductionEnvironment()) {
        next({ name: 'PageNotPermission' });

        return;
      }

      if (SUPER_ADMIN_ONLY_ROUTES.includes(to.name)) {
        if (Number(user?.profileId) !== PROFILES.ADMINISTRATIVE.id) {
          next({ name: 'PageNotPermission' });

          return;
        }
      }

      next();
    } else {
      storage.saveReturnPath(to.fullPath);
      next({ name: ROUTE_NAMES.LOGIN });
    }
  } else {
    next();
  }
});

// Tratamento de falha de carregamento de chunks (code splitting)
// Quando um chunk JS nao carrega (rede lenta, deploy recente), o router nao resolve a rota
// e redireciona pro 404. O reload forca o browser a buscar os chunks novos.
router.onError((error) => {
  const isChunkError = error.message?.includes('Loading chunk')
    || error.message?.includes('Failed to fetch')
    || error.message?.includes('Importing a module script failed')
    || error.type === 'missing';

  if (isChunkError) window.location.reload();
});

export default router;
